import type { OrderStatus } from '../types/order';
import type {
  CourierAssignableOrderStatus,
  PublicCourierManifestOrder,
  PublicCourierManifestResult,
  PublicCourierPortalLocalityNames,
  PublicCourierPortalResult,
} from '../types/courier';
import { COURIER_ASSIGNABLE_ORDER_STATUSES } from '../types/courier';
import type { SupportedLocale } from '../i18n';

interface CourierManifestAddressSource {
  readonly address: {
    readonly recipient: string;
    readonly line_1: string;
    readonly line_2: string | null;
    readonly city: string;
    readonly region: string;
    readonly postal_code: string;
    readonly country_code: string;
  };
}

export function courierDeliveryManifestPath(batchId: string, fragmentSecret: string): string {
  const normalizedBatchId = batchId.trim();
  const normalizedSecret = fragmentSecret.trim();
  if (!normalizedBatchId || !normalizedSecret) {
    throw new Error('courierDeliveryManifestPath requires batchId and fragmentSecret');
  }
  return `/courier/deliveries/${encodeURIComponent(normalizedBatchId)}#${encodeURIComponent(normalizedSecret)}`;
}

export function buildCourierDeliveryManifestUrl(
  baseUrl: string,
  batchId: string,
  fragmentSecret: string,
): string {
  const origin = new URL(baseUrl);
  if (origin.pathname !== '/' || origin.search || origin.hash) {
    throw new Error('baseUrl must be an origin without path, query, or hash');
  }
  return `${origin.origin}${courierDeliveryManifestPath(batchId, fragmentSecret)}`;
}

export function parseCourierDeliveryFragment(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    return decoded.trim() === '' ? null : decoded;
  } catch {
    return null;
  }
}

export function courierPortalPath(portalLinkId: string, fragmentSecret: string): string {
  const normalizedPortalLinkId = portalLinkId.trim();
  const normalizedSecret = fragmentSecret.trim();
  if (!normalizedPortalLinkId || !normalizedSecret) {
    throw new Error('courierPortalPath requires portalLinkId and fragmentSecret');
  }
  return `/courier/p/${encodeURIComponent(normalizedPortalLinkId)}#${encodeURIComponent(normalizedSecret)}`;
}

export function buildCourierPortalUrl(
  baseUrl: string,
  portalLinkId: string,
  fragmentSecret: string,
): string {
  const origin = new URL(baseUrl);
  if (origin.pathname !== '/' || origin.search || origin.hash) {
    throw new Error('baseUrl must be an origin without path, query, or hash');
  }
  return `${origin.origin}${courierPortalPath(portalLinkId, fragmentSecret)}`;
}

export function parseCourierPortalFragment(hash: string): string | null {
  return parseCourierDeliveryFragment(hash);
}

export function publicCourierPortalLocalityName(
  locality: PublicCourierPortalLocalityNames,
  locale: SupportedLocale,
): string {
  if (locale === 'ar') return locality.ar ?? locality.he;
  if (locale === 'en') return locality.en ?? locality.he;
  return locality.he;
}

export function formatCourierManifestAddress(order: CourierManifestAddressSource): string {
  return [
    order.address.recipient,
    order.address.line_1,
    order.address.line_2,
    order.address.city,
    order.address.region,
    order.address.postal_code,
    order.address.country_code,
  ]
    .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    .join(', ');
}

export function isCourierAssignableOrderStatus(
  status: OrderStatus,
): status is CourierAssignableOrderStatus {
  return COURIER_ASSIGNABLE_ORDER_STATUSES.includes(status as CourierAssignableOrderStatus);
}

export function formatCourierPackageTag(rawTag: string): string {
  const normalized = rawTag.trim().replace(/^#/, '').toUpperCase();
  return isValidCourierPackageTag(normalized) ? `#${normalized}` : rawTag;
}

export function isValidCourierPackageTag(rawTag: string): boolean {
  return /^[A-F0-9]{8}$/.test(rawTag.trim().replace(/^#/, '').toUpperCase());
}

export function isCourierProofReplacementAllowed(order: PublicCourierManifestOrder): boolean {
  return (
    order.status === 'delivered' &&
    order.proof_status !== 'internally_confirmed' &&
    order.proof_replacement_allowed
  );
}

export function makeCourierProofSubmissionId(): string {
  return crypto.randomUUID();
}

export function assertPublicManifestPrivacy(manifest: PublicCourierManifestResult): void {
  const forbidden = [
    'items',
    'product',
    'product_id',
    'price',
    'store_id',
    'user_id',
    'courier_id',
    'events',
    'inventory',
    'variant',
    'storage_path',
  ];
  const visit = (value: unknown, path: string): void => {
    if (Array.isArray(value)) {
      value.forEach((item, index) => visit(item, `${path}[${index}]`));
      return;
    }
    if (!value || typeof value !== 'object') return;
    for (const [key, nested] of Object.entries(value)) {
      const normalizedKey = key.toLowerCase();
      if (forbidden.some((blocked) => normalizedKey.includes(blocked))) {
        throw new Error(`Public courier manifest leaked ${key} at ${path}`);
      }
      visit(nested, `${path}.${key}`);
    }
  };
  visit(manifest, 'manifest');
}

export function assertPublicCourierPortalPrivacy(result: PublicCourierPortalResult): void {
  const forbidden = [
    'items',
    'product',
    'product_id',
    'price',
    'store_id',
    'user_id',
    'courier_id',
    'order_id',
    'customer_id',
    'locality_id',
    'batch_id',
    'batch_order_id',
    'link_id',
    'events',
    'inventory',
    'variant',
    'storage_path',
    'internal',
    'secret',
    'nonce',
    'hash',
  ];
  const visit = (value: unknown, path: string): void => {
    if (Array.isArray(value)) {
      value.forEach((item, index) => visit(item, `${path}[${index}]`));
      return;
    }
    if (!value || typeof value !== 'object') return;
    for (const [key, nested] of Object.entries(value)) {
      const normalizedKey = key.toLowerCase();
      const isAllowedDisplayId = normalizedKey === 'display_id';
      if (!isAllowedDisplayId && forbidden.some((blocked) => normalizedKey.includes(blocked))) {
        throw new Error(`Public courier portal leaked ${key} at ${path}`);
      }
      visit(nested, `${path}.${key}`);
    }
  };
  visit(result, 'portal');
}
